import React, { useEffect, useState } from 'react';
import { CuteRobot } from './FlyingRobots';

/**
 * NetworkGraph — animated live graph of every system Grocera orchestrates.
 * Signals travel as glowing pulses along curved links to and from a central,
 * pulsing core. Nodes breathe, orbits rotate, and a rotating tick occasionally
 * escalates a random node to "AT RISK" so the graph feels alive.
 */

const initialNodes = [
  { id: 'buyer',       label: 'Buyer',       status: 'CONFIRMED', pos: [18, 14], icon: 'people' },
  { id: 'po',          label: 'PO',          status: 'CONFIRMED', pos: [30, 6],  icon: 'box' },
  { id: 'supplier',    label: 'Supplier',    status: 'PENDING',   pos: [52, 4],  icon: 'truck' },
  { id: 'whatsapp',    label: 'WhatsApp',    status: 'CONFIRMED', pos: [76, 8],  icon: 'chart' },
  { id: 'contract',    label: 'Contract',    status: 'CONFIRMED', pos: [90, 26], icon: 'tag' },
  { id: 'invoice',     label: 'Invoice',     status: 'PENDING',   pos: [92, 50], icon: 'pos' },
  { id: 'delivery',    label: 'Delivery',    status: 'PENDING',   pos: [86, 74], icon: 'bike' },
  { id: 'receipt',     label: 'Receipt',     status: 'CONFIRMED', pos: [68, 90], icon: 'heart' },
  { id: 'reconcile',   label: 'Reconcile',   status: 'CONFIRMED', pos: [46, 94], icon: 'cart' },
  { id: 'shortship',   label: 'Short-ship',  status: 'CONFIRMED', pos: [26, 90], icon: 'trash' },
  { id: 'reliability', label: 'Reliability', status: 'AT RISK',   pos: [10, 74], icon: 'snow' },
  { id: 'claim',       label: 'Claim',       status: 'PENDING',   pos: [4, 48],  icon: 'cloud' },
];

const iconPath = (name) => {
  switch (name) {
    case 'pos':      return <><rect x="-6" y="-5" width="12" height="10" rx="1" /><line x1="-6" y1="-1" x2="6" y2="-1" /></>;
    case 'cart':     return <><path d="M-6 -4 H-3 L-1 4 H5" /><circle cx="-1" cy="6" r="1.4" /><circle cx="4" cy="6" r="1.4" /></>;
    case 'truck':    return <><rect x="-6" y="-3" width="8" height="6" /><path d="M2 0 H6 L8 3 H2" /><circle cx="-3" cy="5" r="1.4" /><circle cx="5" cy="5" r="1.4" /></>;
    case 'snow':     return <><line x1="-6" y1="0" x2="6" y2="0" /><line x1="0" y1="-6" x2="0" y2="6" /><line x1="-4" y1="-4" x2="4" y2="4" /><line x1="-4" y1="4" x2="4" y2="-4" /></>;
    case 'cloud':    return <><path d="M-6 3 Q-6 -3 -1 -3 Q3 -6 6 -1 Q8 3 4 3 Z" /></>;
    case 'tag':      return <><path d="M-6 -6 H0 L6 0 L0 6 L-6 0 Z" /><circle cx="-3" cy="-3" r="1" /></>;
    case 'bike':     return <><circle cx="-4" cy="3" r="2.5" /><circle cx="4" cy="3" r="2.5" /><path d="M-4 3 L0 -3 L4 3" /><line x1="0" y1="-3" x2="2" y2="-3" /></>;
    case 'heart':    return <><path d="M0 5 C-6 0 -6 -5 -3 -5 C-1 -5 0 -3 0 -2 C0 -3 1 -5 3 -5 C6 -5 6 0 0 5 Z" /></>;
    case 'people':   return <><circle cx="-3" cy="-2" r="2" /><circle cx="3" cy="-2" r="2" /><path d="M-6 5 Q-3 1 0 3 Q3 1 6 5" /></>;
    case 'trash':    return <><path d="M-5 -4 H5 L4 6 H-4 Z" /><line x1="-6" y1="-4" x2="6" y2="-4" /><line x1="-2" y1="-6" x2="2" y2="-6" /></>;
    case 'box':      return <><path d="M-6 -3 L0 -6 L6 -3 L6 4 L0 6 L-6 4 Z" /><line x1="-6" y1="-3" x2="0" y2="0" /><line x1="6" y1="-3" x2="0" y2="0" /><line x1="0" y1="0" x2="0" y2="6" /></>;
    case 'chart':    return <><line x1="-6" y1="6" x2="6" y2="6" /><rect x="-5" y="0" width="2" height="6" /><rect x="-1" y="-3" width="2" height="9" /><rect x="3" y="-6" width="2" height="12" /></>;
    default:         return <circle r="4" />;
  }
};

const statusColor = {
  CONFIRMED: 'rgba(255,255,255,0.55)',
  PENDING: 'rgba(255,255,255,0.55)',
  'AT RISK': '#2E6BFF',
};

// Deterministic pseudo-random for stable per-node phases
const seed = (i) => (Math.sin(i * 91.13) * 43758.5453) % 1;

export default function NetworkGraph() {
  const [hover, setHover] = useState(null);
  const [tick, setTick] = useState(0);
  const [pulseFrom, setPulseFrom] = useState(null); // node id currently emitting a burst
  const [ticker, setTicker] = useState([]);
  const [nodes, setNodes] = useState(initialNodes);

  const W = 1200;
  const H = 680;
  const cx = W / 2;
  const cy = H / 2;

  // Global heartbeat
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(id);
  }, []);

  // Random node bursts + status flips + ticker updates
  useEffect(() => {
    const messages = [
      'Supplier CV Segar late confirm · PO-2141',
      'WhatsApp inbound · CV Kebon Fajar',
      'Short-ship detected · PO-2138 · -12 CS',
      'Substitution proposed · Apel Fuji → Apel Malang',
      'Contract match · PO-2140 · price -3.5%',
      'Delivery ETA slip · driver 08',
      'PO-2143 auto-confirmed · CV Segar',
      'Reliability update · CV Sumber Rejeki 91',
      'Claim raised · UD Bunda Alam · $180',
      'Approval waiting · price above threshold',
      'Reconciled · PO-2135 · leakage $42',
      'Buyer decision needed · new supplier',
    ];
    const id = setInterval(() => {
      const pool = initialNodes.map((n) => n.id);
      const chosen = pool[Math.floor(Math.random() * pool.length)];
      setPulseFrom(chosen);
      setTicker((prev) => {
        const msg = messages[Math.floor(Math.random() * messages.length)];
        const time = new Date().toLocaleTimeString('en-GB', { hour12: false });
        const next = [{ id: `${Date.now()}-${chosen}`, time, msg }, ...prev].slice(0, 5);
        return next;
      });
      // random escalation
      if (Math.random() < 0.35) {
        setNodes((prev) =>
          prev.map((n) => {
            if (n.id === chosen && n.status !== 'AT RISK') {
              return { ...n, status: Math.random() < 0.5 ? 'AT RISK' : 'PENDING' };
            }
            return n;
          })
        );
      } else if (Math.random() < 0.25) {
        // resolve a random at-risk
        setNodes((prev) => {
          const risky = prev.filter((n) => n.status === 'AT RISK');
          if (!risky.length) return prev;
          const pick = risky[Math.floor(Math.random() * risky.length)];
          return prev.map((n) => (n.id === pick.id ? { ...n, status: 'CONFIRMED' } : n));
        });
      }
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="section text-white border-t border-white/5 relative overflow-hidden">
      <div className="halftone" />
      <div className="mx-auto max-w-[1400px] px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-6">Agentic AI, orchestrated</p>
            <h2 className="h-display text-[48px] md:text-[64px] leading-[1.02] text-white max-w-[560px]">
              Every signal, every store, one brain.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-6 space-y-5">
            <p className="text-white/60 text-[15px] leading-[1.7] max-w-[520px]">
              A network of agentic AI — one agent for ordering, one for reliability, one for reconciliation — works behind the scenes and talks to itself in real time. POS, cold chain, suppliers, weather, promos, delivery, loyalty, labor and finance move together. Grocera keeps the graph in sync, flags what is at risk, and quietly closes the rest.
            </p>
            {/* Live ticker */}
            <div className="border border-white/10 rounded-md bg-black/40 backdrop-blur-sm p-3 max-w-[520px]">
              <div className="flex items-center justify-between mb-2">
                <span className="eyebrow flex items-center gap-2"><span className="dot-live" />Live signal feed</span>
                <span className="mono text-[10px] text-white/40">grocera://net</span>
              </div>
              <ul className="space-y-1">
                {ticker.length === 0 && (
                  <li className="mono text-[11px] text-white/45">listening for signals…</li>
                )}
                {ticker.map((t, i) => (
                  <li
                    key={t.id}
                    className="mono text-[11px] flex items-center gap-3"
                    style={{ opacity: 1 - i * 0.18, transition: 'opacity 400ms ease' }}
                  >
                    <span className="text-[#2E6BFF]">{t.time}</span>
                    <span className="text-white/70 truncate">{t.msg}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="relative w-full">
          {/* Radial glow behind graph */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(46,107,255,0.35) 0%, rgba(46,107,255,0.10) 30%, rgba(46,107,255,0) 65%)',
            }}
          />

          {/* Robot mascot in the empty top-left corner, sized in % so it
              scales with the graph and never lands on the Buyer node. */}
          <div className="absolute pointer-events-none [&>svg]:w-full [&>svg]:h-auto" style={{ left: '3%', top: '12%', width: '5%' }}>
            <CuteRobot blink={false} wave={false} />
          </div>

          <svg viewBox={`0 0 ${W} ${H}`} className="relative w-full h-auto block">
            <defs>
              <radialGradient id="core" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#a8c3ff" />
                <stop offset="55%" stopColor="#2E6BFF" />
                <stop offset="100%" stopColor="#0b2a80" />
              </radialGradient>
              <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="6" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="glow-soft" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {/* Reusable link gradient — data pulse trail */}
              <linearGradient id="pulseGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2E6BFF" stopOpacity="0" />
                <stop offset="50%" stopColor="#7ba0ff" stopOpacity="1" />
                <stop offset="100%" stopColor="#2E6BFF" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Connection lines with dashed animated flow */}
            {nodes.map((n, i) => {
              const x = (n.pos[0] / 100) * W;
              const y = (n.pos[1] / 100) * H;
              const midX = (x + cx) / 2;
              const midY = (y + cy) / 2 + Math.sin(i * 1.7) * 30;
              const path = `M ${x} ${y} Q ${midX} ${midY} ${cx} ${cy}`;
              const isRisk = n.status === 'AT RISK';
              const isHot = pulseFrom === n.id;
              const stroke = isRisk ? '#2E6BFF' : 'rgba(255,255,255,0.22)';
              const strokeWidth = isRisk ? 1.6 : 0.8;
              const opacity = hover && hover !== n.id ? 0.12 : 1;

              return (
                <g key={`link-${n.id}`} opacity={opacity}>
                  {/* Base line */}
                  <path
                    id={`link-${n.id}`}
                    d={path}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    fill="none"
                  />
                  {/* Animated dash overlay (data flowing to center) */}
                  <path
                    d={path}
                    stroke={isRisk ? '#7ba0ff' : 'rgba(255,255,255,0.55)'}
                    strokeWidth={isRisk ? 1.6 : 0.9}
                    fill="none"
                    strokeDasharray="4 10"
                    style={{
                      strokeDashoffset: (tick * (isRisk ? 3 : 1.5)) % 400,
                    }}
                    opacity={isRisk ? 0.9 : 0.35}
                  />
                  {/* Traveling pulse dot */}
                  <circle r={isRisk ? 3 : 2} fill={isRisk ? '#7ba0ff' : '#ffffff'} filter="url(#glow-soft)">
                    <animateMotion
                      dur={`${isRisk ? 2.2 : 4 + seed(i) * 2}s`}
                      repeatCount="indefinite"
                      keyPoints="0;1"
                      keyTimes="0;1"
                      calcMode="linear"
                    >
                      <mpath href={`#link-${n.id}`} />
                    </animateMotion>
                    <animate attributeName="opacity" values="0.2;1;0.2" dur={`${isRisk ? 2.2 : 4}s`} repeatCount="indefinite" />
                  </circle>
                  {/* Second pulse offset for busier links */}
                  {isRisk && (
                    <circle r="2.4" fill="#ffffff" filter="url(#glow-soft)">
                      <animateMotion dur="2.2s" repeatCount="indefinite" begin="1.1s">
                        <mpath href={`#link-${n.id}`} />
                      </animateMotion>
                    </circle>
                  )}
                  {/* Burst wave when node is currently emitting */}
                  {isHot && (
                    <circle r="4" fill="#ffffff" filter="url(#glow)">
                      <animateMotion dur="1s" repeatCount="1" fill="freeze">
                        <mpath href={`#link-${n.id}`} />
                      </animateMotion>
                      <animate attributeName="r" values="6;2" dur="1s" fill="freeze" />
                      <animate attributeName="opacity" values="1;0" dur="1s" fill="freeze" />
                    </circle>
                  )}
                </g>
              );
            })}

            {/* Faint secondary lattice between neighboring nodes */}
            {nodes.map((n, i) => {
              const next = nodes[(i + 3) % nodes.length];
              const x1 = (n.pos[0] / 100) * W;
              const y1 = (n.pos[1] / 100) * H;
              const x2 = (next.pos[0] / 100) * W;
              const y2 = (next.pos[1] / 100) * H;
              return (
                <line
                  key={`web-${n.id}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth="0.5"
                />
              );
            })}

            {/* Rotating orbit rings around the core */}
            <g transform={`translate(${cx}, ${cy})`}>
              <g>
                <circle r="120" fill="none" stroke="rgba(255,255,255,0.10)" strokeDasharray="1 6" />
                <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="24s" repeatCount="indefinite" />
              </g>
              <g>
                <circle r="150" fill="none" stroke="rgba(46,107,255,0.22)" strokeDasharray="2 10" />
                {/* satellites */}
                <circle cx="150" cy="0" r="2.5" fill="#7ba0ff" filter="url(#glow-soft)" />
                <circle cx="-150" cy="0" r="2" fill="#7ba0ff" filter="url(#glow-soft)" />
                <animateTransform attributeName="transform" type="rotate" from="0" to="-360" dur="36s" repeatCount="indefinite" />
              </g>
              <g>
                <circle r="180" fill="none" stroke="rgba(255,255,255,0.06)" strokeDasharray="3 14" />
                <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="60s" repeatCount="indefinite" />
              </g>
            </g>

            {/* Expanding shockwave rings from core */}
            <g transform={`translate(${cx}, ${cy})`}>
              <circle r="80" fill="none" stroke="#2E6BFF" strokeWidth="1.2" opacity="0">
                <animate attributeName="r" values="80;180" dur="3.6s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0" dur="3.6s" repeatCount="indefinite" />
              </circle>
              <circle r="80" fill="none" stroke="#2E6BFF" strokeWidth="1" opacity="0">
                <animate attributeName="r" values="80;180" dur="3.6s" begin="1.2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.5;0" dur="3.6s" begin="1.2s" repeatCount="indefinite" />
              </circle>
              <circle r="80" fill="none" stroke="#2E6BFF" strokeWidth="1" opacity="0">
                <animate attributeName="r" values="80;180" dur="3.6s" begin="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.4;0" dur="3.6s" begin="2.4s" repeatCount="indefinite" />
              </circle>
            </g>

            {/* Central node */}
            <g filter="url(#glow)">
              <circle cx={cx} cy={cy} r="82" fill="url(#core)" opacity="0.95">
                <animate attributeName="r" values="80;84;80" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={cx} cy={cy} r="82" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
              <circle cx={cx} cy={cy} r="96" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8" />
            </g>
            <g>
              <path
                d={`M ${cx - 10} ${cy - 12} L ${cx} ${cy - 6} L ${cx + 10} ${cy - 12} L ${cx + 6} ${cy - 2} L ${cx + 12} ${cy + 6} L ${cx} ${cy + 2} L ${cx - 12} ${cy + 6} L ${cx - 6} ${cy - 2} Z`}
                fill="#ffffff"
                opacity="0.95"
              />
              <text
                x={cx} y={cy + 22}
                fontFamily="JetBrains Mono, monospace"
                fontSize="12" textAnchor="middle"
                fill="#ffffff" letterSpacing="2"
              >GROCERA</text>
              <text
                x={cx} y={cy + 40}
                fontFamily="JetBrains Mono, monospace"
                fontSize="9" textAnchor="middle"
                fill="rgba(255,255,255,0.75)" letterSpacing="2"
              >THE COMPANY BRAIN</text>
            </g>

            {/* Supplier sub-branches — top Indonesian suppliers that feed into the Supplier node */}
            {(() => {
              const supNode = nodes.find((n) => n.id === 'supplier');
              if (!supNode) return null;
              const px = (supNode.pos[0] / 100) * W;
              const py = (supNode.pos[1] / 100) * H;
              const subs = [
                { name: 'CV Segar',  dx: -50, dy: -70 },
                { name: 'Kebon Fajar', dx:  20, dy: -80 },
                { name: 'Bunda Alam', dx:  90, dy: -60 },
                { name: 'Sumber R.', dx: 130, dy:  10 },
              ];
              const dim = hover && hover !== 'supplier' ? 0.15 : 1;
              return (
                <g opacity={dim}>
                  {subs.map((s, si) => {
                    const sx = px + s.dx;
                    const sy = py + s.dy;
                    const pid = `sup-sub-${si}`;
                    return (
                      <g key={pid}>
                        <path
                          id={pid}
                          d={`M ${sx} ${sy} Q ${(sx + px) / 2} ${(sy + py) / 2 + 6} ${px} ${py}`}
                          stroke="rgba(255,255,255,0.25)"
                          strokeWidth="0.7"
                          strokeDasharray="3 5"
                          fill="none"
                          style={{ strokeDashoffset: (tick * 2 + si * 20) % 400 }}
                        />
                        <circle r="1.6" fill="#ffffff" filter="url(#glow-soft)">
                          <animateMotion dur={`${3 + si * 0.5}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1" calcMode="linear">
                            <mpath href={`#${pid}`} />
                          </animateMotion>
                        </circle>
                        <g transform={`translate(${sx}, ${sy})`}>
                          <rect x="-38" y="-11" width="76" height="22" rx="2" fill="rgba(5,5,6,0.9)" stroke="rgba(255,255,255,0.18)" />
                          <text x="0" y="4" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#ffffff">{s.name}</text>
                        </g>
                      </g>
                    );
                  })}
                </g>
              );
            })()}

            {/* Nodes */}
            {nodes.map((n, i) => {
              const x = (n.pos[0] / 100) * W;
              const y = (n.pos[1] / 100) * H;
              const dim = hover && hover !== n.id ? 0.35 : 1;
              const isRisk = n.status === 'AT RISK';
              const isHot = pulseFrom === n.id;
              const breathe = 1 + Math.sin(tick * 0.06 + i) * 0.03;
              return (
                <g
                  key={n.id}
                  transform={`translate(${x}, ${y}) scale(${breathe})`}
                  opacity={dim}
                  onMouseEnter={() => setHover(n.id)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 300ms ease' }}
                >
                  {/* Outer ping when hot */}
                  {isHot && (
                    <circle r="26" fill="none" stroke="#2E6BFF" strokeWidth="1.4">
                      <animate attributeName="r" values="26;50" dur="1s" fill="freeze" />
                      <animate attributeName="opacity" values="1;0" dur="1s" fill="freeze" />
                    </circle>
                  )}
                  {/* Ring */}
                  <circle
                    r="26"
                    fill="rgba(5,5,6,0.85)"
                    stroke={isRisk ? '#2E6BFF' : 'rgba(255,255,255,0.35)'}
                    strokeWidth={isRisk ? 1.6 : 1}
                  />
                  {isRisk && (
                    <circle r="30" fill="none" stroke="rgba(46,107,255,0.45)" strokeWidth="1">
                      <animate attributeName="r" values="30;36;30" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.7;0.2;0.7" dur="2s" repeatCount="indefinite" />
                    </circle>
                  )}
                  {/* Icon */}
                  <g stroke="rgba(255,255,255,0.9)" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    {iconPath(n.icon)}
                  </g>
                  {/* Status dot */}
                  <circle
                    cx="20" cy="-18" r="6"
                    fill={isRisk ? '#2E6BFF' : 'rgba(255,255,255,0.12)'}
                    stroke="rgba(0,0,0,0.6)" strokeWidth="0.6"
                  >
                    {isRisk && (
                      <animate attributeName="opacity" values="1;0.4;1" dur="1.4s" repeatCount="indefinite" />
                    )}
                  </circle>

                  {/* Label plate */}
                  <g transform="translate(0, 44)">
                    <rect
                      x="-46" y="-2" width="92" height="36" rx="2"
                      fill="rgba(5,5,6,0.9)"
                      stroke={isRisk ? 'rgba(46,107,255,0.35)' : 'rgba(255,255,255,0.08)'}
                    />
                    <text x="0" y="12" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="12" fill="#ffffff">
                      {n.label}
                    </text>
                    <text
                      x="0" y="26" textAnchor="middle"
                      fontFamily="JetBrains Mono, monospace" fontSize="8.5"
                      letterSpacing="1.5" fill={statusColor[n.status]}
                    >
                      {n.status}
                    </text>
                  </g>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#2E6BFF]" />
            <span className="mono text-[11px] text-white/70 uppercase tracking-wider">At risk · Grocera intervenes</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-white/25" />
            <span className="mono text-[11px] text-white/70 uppercase tracking-wider">In sync</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="mono text-[11px] text-white/40">
              {nodes.filter((n) => n.status === 'AT RISK').length} at risk ·{' '}
              {nodes.filter((n) => n.status === 'PENDING').length} pending ·{' '}
              {nodes.filter((n) => n.status === 'CONFIRMED').length} in sync
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
